import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const emptyRow = () => ({ JContainerDetailID: null, ContainerTypeID: null, ContainerNo: '', SealNo: '', Description: '' });

const ContainerTab = ({ data, masterData, onChange }) => {
    const [containers, setContainers] = useState(data?.length ? data : [emptyRow()]);
    const containerTypes = masterData.containerTypes || [];

    useEffect(() => {
        if (!data || data.length === 0) {
            const def = [emptyRow()];
            setContainers(def); onChange(def);
        } else {
            setContainers(data);
        }
    }, [data]);

    const addNewRow = () => {
        const updated = [...containers, emptyRow()];
        setContainers(updated); onChange(updated);
    };

    const deleteRow = (index) => {
        if (containers.length === 1) { alert('Cannot delete the last row. At least one container entry is required.'); return; }
        if (window.confirm('Delete this container?')) {
            const updated = containers.filter((_, i) => i !== index);
            setContainers(updated); onChange(updated);
        }
    };

    const updateRow = (index, field, value) => {
        const updated = [...containers];
        updated[index][field] = value;
        setContainers(updated); onChange(updated);
    };

    const cellInput    = `w-full px-2.5 py-2 border border-[#CBD5E1] rounded text-sm outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] font-[inherit]`;
    const cellSelect   = `w-full px-2.5 py-2 border border-[#CBD5E1] rounded text-sm outline-none cursor-pointer bg-white transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] font-[inherit]`;
    const textareaCls  = `w-full min-h-[50px] px-2.5 py-2 border border-[#CBD5E1] rounded text-sm resize-y outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] font-[inherit]`;

    return (
        <div className="p-5 bg-white rounded-lg flex flex-col h-full gap-4">

            {/* Toolbar */}
            <div className="flex items-center justify-between flex-shrink-0">
                <h3 className="text-base font-semibold text-gray-800 m-0">Container Details</h3>
                <button onClick={addNewRow}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5"
                    style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                    <Plus size={15} /> Add Container
                </button>
            </div>

            {/* No container types warning */}
            {containerTypes.length === 0 && (
                <div className="flex-shrink-0 px-4 py-3 rounded-r-lg text-sm" style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderLeft: '4px solid #F59E0B', color: '#B45309' }}>
                    <strong>Warning:</strong> No container types available. Please add container types in the master data first.
                </div>
            )}

            {/* Grid */}
            <div className="flex-1 overflow-auto border border-[#E2E8F0] rounded-lg">
                <table className="w-full border-collapse text-sm min-w-[700px]">
                    <thead className="sticky top-0 z-10"
                        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                        <tr>
                            {[
                                { label: '#',                w: 'w-10'          },
                                { label: 'Container Type *', w: 'w-48'          },
                                { label: 'Container No',     w: 'w-44'          },
                                { label: 'Seal No',          w: 'w-36'          },
                                { label: 'Description',      w: 'min-w-[220px]' },
                                { label: 'Action',           w: 'w-16'          },
                            ].map(({ label, w }) => (
                                <th key={label} className={`${w} px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-white border-r border-white/20 last:border-r-0`}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {containers.length === 0 ? (
                            <tr><td colSpan="6" className="py-10 text-center text-gray-400 text-sm">No containers added. Click "Add Container" to begin.</td></tr>
                        ) : (
                            containers.map((container, index) => (
                                <tr key={index} className="border-b border-gray-100 hover:bg-[#EFF6FF] transition-colors duration-150">
                                    <td className="px-3 py-2.5 text-center font-bold text-[#2563EB]">{index + 1}</td>
                                    <td className="px-2 py-2">
                                        <select value={container.ContainerTypeID || ''}
                                            onChange={e => updateRow(index, 'ContainerTypeID', parseInt(e.target.value) || null)}
                                            className={cellSelect}>
                                            <option value="">Select Type</option>
                                            {containerTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="text" value={container.ContainerNo || ''}
                                            onChange={e => updateRow(index, 'ContainerNo', e.target.value)}
                                            placeholder="Container number" maxLength="20" className={cellInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="text" value={container.SealNo || ''}
                                            onChange={e => updateRow(index, 'SealNo', e.target.value)}
                                            placeholder="Seal number" maxLength="20" className={cellInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <textarea value={container.Description || ''} rows={2}
                                            onChange={e => updateRow(index, 'Description', e.target.value)}
                                            placeholder="Additional details" className={textareaCls} />
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                        <button onClick={() => deleteRow(index)} title="Delete"
                                            disabled={containers.length === 1}
                                            className="p-1.5 rounded border-0 cursor-pointer bg-transparent transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                                            style={{ color: '#DC2626' }}
                                            onMouseEnter={(e) => { if (containers.length !== 1) { e.currentTarget.style.backgroundColor = '#FEE2E2'; e.currentTarget.style.color = '#B91C1C'; } }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#DC2626'; }}>
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            <div className="flex-shrink-0 flex items-center gap-4 flex-wrap px-4 py-3 bg-gray-50 border border-[#E2E8F0] rounded-lg text-sm">
                <span className="font-semibold text-gray-700">
                    Total Containers: <span className="font-bold text-gray-900">{containers.length}</span>
                </span>
                {containerTypes.some(ct => containers.some(c => c.ContainerTypeID === ct.id)) && (
                    <span className="text-gray-500 font-medium">By Type:</span>
                )}
                {containerTypes.map(ct => {
                    const count = containers.filter(c => c.ContainerTypeID === ct.id).length;
                    if (!count) return null;
                    return (
                        <span key={ct.id}
                            className="px-2.5 py-1 text-[12px] font-bold rounded-full"
                            style={{ background: '#DBEAFE', color: '#2563EB' }}>
                            {ct.name}: {count}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default ContainerTab;